import { type Graphic } from './graphic';

/**
 * Represents a cached entry for a `(Graphic, property)` pair in a `GraphicLruCache`.
 */
interface Entry {
  /**
   * The cached value associated with the `(Graphic, property)` pair.
   */
  value: unknown;
  /**
   * The corresponding node in the LRU linked list for this entry.
   */
  node: LruNode;
}

/**
 * A doubly-linked list node used for tracking LRU order in a `GraphicLruCache`.
 */
interface LruNode {
  /**
   * Pointer to the previous node in the LRU list, or `undefined` if this is the head.
   */
  prev?: LruNode;
  /**
   * Pointer to the next node in the LRU list, or `undefined` if this is the tail.
   */
  next?: LruNode;
  /**
   * The property name associated with this node's cached value.
   */
  property: string;
  /**
   * A weak reference to the `Graphic` instance associated with this node's cached value.
   */
  targetRef: WeakRef<Graphic>;
  /**
   * A unique identifier for this node, used to register cleanup handlers with
   * `FinalizationRegistry`. This is always a unique empty object.
   */
  id: object;
}

/**
 * @summary A cache for storing computed properties associated with `Graphic` instances.
 *
 * Allows per-Graphic caching of arbitrary properties (by string key) with a maximum
 * capacity and LRU eviction policy. Uses `WeakRef` and `FinalizationRegistry` to avoid
 * memory leaks: when a `Graphic` is garbage collected, its associated cache entries are
 * automatically removed.
 */
export class GraphicLruCache {
  /**
   * The maximum number of `(Graphic, property)` entries the cache can hold.
   */
  private capacity = 0;

  /**
   * The main storage structure: a `WeakMap` mapping each `Graphic` to a map of cached
   * properties for that graphic. Each property's value is an `Entry` containing the
   * cached value and its corresponding LRU node.
   */
  private buckets = new WeakMap<Graphic, Map<string, Entry>>();

  /**
   * Pointer to the head of the doubly-linked LRU list (least-recently used entry).
   */
  private head?: LruNode;

  /**
   * Pointer to the tail of the doubly-linked LRU list (most-recently used entry).
   */
  private tail?: LruNode;

  /**
   * Maps the `id` of each `LruNode` to the node itself, for quick lookup during cleanup
   * when a `Graphic` is garbage collected.
   */
  private nodesById = new Map<object, LruNode>();

  /**
   * Used to detect when a `Graphic` is garbage collected, so we can remove its
   * associated cache entries. When a `Graphic` is collected, the cleanup callback
   * receives the `id` of the corresponding `LruNode` to evict it from the cache.
   */
  private finalizer: FinalizationRegistry<object>;

  constructor(capacity: number) {
    this.maxSize = capacity;

    // When a Graphic is garbage collected, we need to evict all its entries from the
    // cache. We achieve this by registering each LRU node with a FinalizationRegistry,
    // using the Graphic as the target.
    this.finalizer = new FinalizationRegistry<object>((id) => {
      const node = this.nodesById.get(id);
      if (!node) {
        // Already evicted
        return;
      }

      // Remove the node from LRU. No need to touch the weak map, as the Graphic is
      // already gone.
      this.removeNode(node);
      this.nodesById.delete(id);
    });
  }

  /**
   * The current number of `(Graphic, property)` entries in the cache.
   */
  get size(): number {
    return this.nodesById.size;
  }

  /**
   * The maximum number of `(Graphic, property)` entries the cache can hold before
   * least-recently used entries are evicted.
   */
  get maxSize(): number {
    return this.capacity;
  }

  /**
   * Set a new maximum capacity for the cache. If the new capacity is smaller than
   * the current size, least-recently used entries will be evicted immediately.
   */
  set maxSize(newCap: number) {
    if (newCap <= 0 || !Number.isFinite(newCap)) {
      throw new Error(`capacity must be a positive finite number`);
    }

    this.capacity = Math.round(newCap);
    this.trimToCapacity();
  }

  /**
   * Gets the cached value for the given `(Graphic, property)` pair if present. If not
   * present, calls the `initializer` function to compute the value, stores it in the
   * cache, and returns it.
   *
   * Accessing a cached entry updates its recency.
   *
   * @param graphic The `Graphic` instance to get the property for.
   * @param property The property name to get.
   * @param initializer A function that computes the property's value if not cached.
   * @returns The cached or newly computed value for the `(Graphic, property)` pair.
   */
  getOrInsertProperty<T>(
    graphic: Graphic,
    property: string,
    initializer: (graphic: Graphic, property: string) => T
  ): T {
    let bucket = this.buckets.get(graphic);
    if (bucket) {
      const hit = bucket.get(property);
      if (hit) {
        // Entry already cached - move to end to mark as most-recently used
        this.moveNodeToEnd(hit.node);
        return hit.value as T;
      }
    }

    // Cache miss - compute the value and store it
    const value = initializer(graphic, property);
    const nodeId = {};
    const node: LruNode = {
      id: nodeId,
      property,
      targetRef: new WeakRef(graphic),
    };

    this.appendNewNode(node);
    this.nodesById.set(nodeId, node);

    // Create bucket if needed
    if (!bucket) {
      bucket = new Map<string, Entry>();
      this.buckets.set(graphic, bucket);
    }

    bucket.set(property, { value, node });

    // Register the LRU node for removal when the Graphic is garbage collected
    this.finalizer.register(graphic, nodeId, nodeId);

    // Evict least-recently used entries if we're now over capacity
    this.trimToCapacity();

    return value;
  }

  /**
   * Deletes the cached entry for the given `(Graphic, property)` pair if it exists.
   *
   * This does not need to be called explicitly in most cases, as entries will be
   * automatically evicted when the cache exceeds capacity or when the `Graphic` is
   * garbage collected.
   *
   * @param graphic The `Graphic` instance whose property to delete.
   * @param property The property name to delete.
   * @returns `true` if an entry was deleted, `false` if no entry existed.
   */
  delete(graphic: Graphic, property: string): boolean {
    const bucket = this.buckets.get(graphic);
    if (!bucket) {
      return false;
    }

    const entry = bucket.get(property);
    if (!entry) {
      return false;
    }

    // Evict the node and entry
    this.evictNode(entry.node);
    return true;
  }

  /**
   * Removes all entries from the cache.
   */
  clear(): void {
    // Unregister all nodes from FinalizationRegistry
    for (const node of this.nodesById.values()) {
      this.finalizer.unregister(node.id);
    }

    // Reset the LRU linked list
    this.head = undefined;
    this.tail = undefined;
    this.nodesById.clear();

    // Clear all buckets
    this.buckets = new WeakMap();
  }

  /**
   * Appends a node to the tail of the LRU list, marking it as most-recently used.
   *
   * @param node The node to append to the tail. Must not already be in the list.
   */
  private appendNewNode(node: LruNode): void {
    if (!this.tail) {
      this.head = this.tail = node;
    } else {
      node.prev = this.tail;
      this.tail.next = node;
      this.tail = node;
    }
  }

  /**
   * Removes a node from the LRU list. If the node has already been removed, this is a
   * no-op.
   *
   * @param node The node to remove from the list.
   */
  private removeNode(node: LruNode): void {
    // If the node is already removed, do nothing
    const alreadyRemoved =
      node.prev === undefined &&
      node.next === undefined &&
      this.head !== node &&
      this.tail !== node;
    if (alreadyRemoved) {
      return;
    }

    const { prev, next } = node;
    if (prev) {
      prev.next = next;
    } else {
      this.head = next;
    }

    if (next) {
      next.prev = prev;
    } else {
      this.tail = prev;
    }

    node.prev = node.next = undefined;
  }

  /**
   * Evicts a specific node from the cache, removing it from the LRU list and removing
   * its associated `(Graphic, property)` entry from the bucket.
   *
   * @param node The node to evict from the cache.
   */
  private evictNode(node: LruNode): void {
    // Remove from LRU list
    this.removeNode(node);
    this.nodesById.delete(node.id);

    // Ensure that the FinalizationRegistry doesn't try to clean it up later
    this.finalizer.unregister(node.id);

    // Try to remove from bucket if the Graphic is still alive
    const graphic = node.targetRef.deref();
    if (graphic) {
      const bucket = this.buckets.get(graphic);
      if (bucket) {
        bucket.delete(node.property);

        if (bucket.size === 0) {
          this.buckets.delete(graphic);
        }
      }
    }
  }

  /**
   * Moves an existing node to the tail of the LRU list, marking it as most-recently
   * used.
   *
   * @param node The node to move to the tail. Must already be in the list.
   */
  private moveNodeToEnd(node: LruNode): void {
    if (node === this.tail) {
      return;
    }

    this.removeNode(node);
    this.appendNewNode(node);
  }

  /**
   * Evicts least-recently used entries until the cache size is within capacity.
   */
  private trimToCapacity(): void {
    while (this.nodesById.size > this.capacity && this.head) {
      this.evictNode(this.head);
    }
  }
}
